import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { SmartImage } from '@/components/SmartImage'
import { WorkflowTabs, type WorkflowTab } from '@/components/WorkflowTabs'
import { GeneratorFrame, PriceConfirmDialog, StudioPanel, SubmitBar } from '@/components/studio'
import { Card, CardBody, CardHead, Chips, Field, Input, Segmented, Textarea } from '@/components/ui'
import { RefUploader } from '@/components/RefUploader'
import { AiFillButton } from '@/components/AiFillButton'
import { ProductAssetPickerButton } from '@/components/ProductAssetPicker'
import { ModelSelect } from '@/components/ModelSelect'
import { DEFAULT_FAMILY_ID } from '@/config/modelFamilies'
import { BANNER_PLATFORMS, BANNER_SIZES, BANNER_TYPES, modelLabel } from '@/config/catalog'
import { getPlatform } from '@/config/platforms'
import {
  TEXT_MODE_LABEL,
  TEXT_MODES,
  buildBannerFillBrief,
  buildBannerPrompt,
  type BannerInput,
} from '@/config/prompts'
import { useJobCenter } from '@/context/JobCenterContext'
import { FILL_FORM_FIELDS, type ProductAsset } from '@/api/types'
import { useDraft } from '@/hooks/useDraft'
import { useGenerateFlow } from '@/hooks/useGenerateFlow'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { usePricePreview } from '@/hooks/usePricePreview'
import { useToast } from '@/context/ToastContext'
import { mergeProductRefs, productImportMessage } from '@/lib/productRefs'

/**
 * 参考图上限 —— **一处真源**：`RefUploader` 的 `max` 与「从商品库带入」的截断同源。
 * 写两个 6 的后果是改一个漏一个，而症状是「带进来 6 张、其实只吃 4 张」。
 */
const PRODUCT_REFS_MAX = 6

const DEFAULTS: BannerInput & {
  model: string
  refs: string[]
  outputs: number
} = {
  platform: 'taobao',
  type: 'promo',
  size: 'pc-hero',
  productName: '',
  mainCopy: '',
  subCopy: '',
  sellingPoints: '',
  extra: '',
  textMode: 'ai',
  resolution: '2k',
  model: DEFAULT_FAMILY_ID.image,
  refs: [],
  outputs: 1,
}

/**
 * 三个页签 + 常驻提交栏。
 *
 * 迭代史（别把上下文丢了）：
 *   v1 三步向导 —— 2026-09-28 撤掉。真问题不是"分得太细"，而是把价格与生成按钮
 *      藏在流程末端，得一路点「下一步」才知道花多少钱。
 *   v2 两个页签 —— 用户 2026-09-29 打回「UI 太乱了，马上精简」。第 1 页签塞了
 *      4 块（产品名独占一卡 + 参考图 + 投放/场景/尺寸糊在一卡 + 文案），
 *      光「场景类型」就把 9 张带描述的卡片竖着铺了一屏。
 *   v3 三个页签 —— 按**决策域**切，不按字段数切：
 *      ① 产品与素材（输入物）② 版式与文案（视觉决策）③ 输出与核对（花钱与确认）。
 *   v4 文案上移（2026-09-30 用户指着「文案与画面」整卡说「这个部分放到 step 的第一个 form」）：
 *      文案（主/副标题、卖点、画面文字、补充要求）本来就是**用户提供的输入物**，
 *      与产品名同类，不该跟「平台 / 场景 / 尺寸」这些视觉决策挤在同一页签 ——
 *      放在第一步意味着「填完产品名顺手把话也写了」，不用先跳到第 2 步。
 *      ⚠️ 页签**数量与顺序都没变**（只是卡片在两个页签之间挪了位置 + 跟着换名），
 *      所以 `step_banner-v2` **不必**升 key —— 落在 index=1 的老草稿看到的仍是
 *      「平台与版式」，不会静默错位（同 FashionStudio 那条「只替换文案不必升 key」）。
 *
 * 这一版砍掉的四处"乱源"：
 *   1. 场景类型 9 张竖排 `.opt` 卡 → `.tool-grid` 宫格（同一种数据，高度砍到 1/3）；
 *   2. 画布尺寸原来左栏一个 `<Select>`、右栏一个「尺寸参考」宫格 —— **同一份 state
 *      两个控件**（违反「同一控件只能有一份实现」）。现在只留表单里的视觉宫格，
 *      右栏那张卡整块删掉；
 *   3. 「产品名称」原来独占一张卡（一个 input 一张卡）→ 与参考图并进第 1 页签；
 *   4. 「将发送的提示词」卡 —— `PriceConfirmDialog` 里**已经有同一个 `<details>`**，
 *      同一内容两处实现，删卡。
 *
 * v5 参考图上移（2026-10-09，用户原话「**产品图放上去**」）：
 *      参考图原来排在第 1 页签的**最底下**（产品名称 → 文案与画面 → 参考图），
 *      要滚到底才看得见。现在移到「产品名称」之后 —— 见 `stepAssets` 里那段注释：
 *      **AI 填充的 brief 里带着 `hasRef`**，参考图排在文案后面时，用户按顺序填到文案
 *      那一步还没传图，AI 只能按「没有参考图」写一版。
 *      ⚠️ 页签数与顺序都没变，只是卡片在**同一页内**换位 ⇒ `step_banner-v2` 不升 key。
 *
 * v6 文案独立成一步（2026-10-09，用户原话「**文案与画面，放到 step2，这块做成 4step**」）：
 *      v4 把文案塞进第 1 页签（与产品名同类，都是「用户提供的输入物」），
 *      但那一页现在有三张卡（产品名称 / 参考图 / 文案与画面），
 *      「文案与画面」自己就占五个字段（主/副标题、卖点、画面文字档位、补充要求）。
 *      现在把它**拆出来单独成一步**，落在第 2 位：
 *        ① 产品与素材（名称 + 参考图）② **文案与画面** ③ 平台与版式 ④ 输出与核对。
 *      ⚠️ **页签数 3 → 4，必须升 `step_banner-v3`**：老草稿的 index=1 在旧结构里是
 *        「平台与版式」、在新结构里是「文案与画面」—— 不升 key 就是静默错位
 *        （用户切回来会发现站在另一页，且不知道自己什么时候跳到这儿的）。
 *      ⚠️ 顺序仍然守住 v5 那条约束：**参考图必须排在文案之前**
 *        （AI 填充的 brief 里带 `hasRef`，写文案时要读它）—— 拆开后 ① 在前、② 在后，
 *        这条依然成立，且比原来更显式。
 */
const PANES: WorkflowTab[] = [
  { id: 'assets', title: '产品与素材', desc: '产品名称与参考图 —— 后面所有设置都建立在这上面' },
  { id: 'copy', title: '文案与画面', desc: '主标题、副标题、卖点与画面文字档位' },
  { id: 'layout', title: '平台与版式', desc: '投放平台、场景类型与画布尺寸' },
  { id: 'review', title: '输出与核对', desc: '模型、分辨率、数量与参数核对，确认后提交' },
]

export function BannerStudio() {
  const { value: form, patch } = useDraft('banner', DEFAULTS)
  // useLocalStorage 自带 hw_ 前缀，不要重复写；步骤进度也持久化，中途切页回来不丢位置。
  // ⚠️ key 带版本号，**每次动页签数或顺序都要升**：v1(2 页签) → v2(3 页签) → v3(4 页签)。
  // 这次 3 → 4 升 v3：老草稿存的 index=1 在 v2 里是「平台与版式」、在 v3 里是「文案与画面」。
  // 不升 key 就是静默错位（用户切回来站在另一页，且不知道什么时候跳的）。
  const [step, setStep] = useLocalStorage<number>('step_banner-v3', 0)
  const { cancel, jobsFor, outputsFor, clearResults } = useJobCenter()
  const flow = useGenerateFlow()
  const toast = useToast()

  /* ------------------------------------------------------------------ *
   * 从商品库带入（2026-10-09）
   *
   * 用户口径：「其他页面也可以引用这个商品库啊」。这一页带入的是
   * **产品名 + 卖点 + 产品图** —— 与详情页/社媒图同一条口径，合并规则复用
   * `lib/productRefs.ts`（商品图在前 + 追加去重 + 如实报截断）。
   *
   * ⚠️ 不新增第二份 state：带入后就是普通的手填值，用户随便改。
   * ------------------------------------------------------------------ */
  const applyProductAsset = useCallback(
    (asset: ProductAsset) => {
      const incoming = asset.images.map((i) => i.url).filter(Boolean)
      const { urls, dropped } = mergeProductRefs(form.refs, incoming, PRODUCT_REFS_MAX)
      patch({ productName: asset.title, sellingPoints: asset.sellingPoints, refs: urls })
      toast.success(
        productImportMessage(asset.title, { refs: urls.length, dropped, max: PRODUCT_REFS_MAX }),
      )
    },
    [form.refs, patch, toast],
  )

  // 侧栏子项用 ?type= / ?size= 直达某种 Banner（直播预告 / 优惠券图 / 竖版促销海报）。
  // 消费掉就把参数删掉 —— 留着会在用户手动换类型后，一刷新又被拽回去。
  const [sp, setSp] = useSearchParams()
  const wantType = sp.get('type')
  const wantSize = sp.get('size')
  useEffect(() => {
    if (!wantType && !wantSize) return
    const next: Partial<BannerInput> = {}
    if (wantType && BANNER_TYPES.some((t) => t.id === wantType)) next.type = wantType
    if (wantSize && BANNER_SIZES.some((s) => s.id === wantSize)) next.size = wantSize
    if (Object.keys(next).length) patch(next)
    const rest = new URLSearchParams(sp)
    rest.delete('type')
    rest.delete('size')
    setSp(rest, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantType, wantSize])

  const size = BANNER_SIZES.find((s) => s.id === form.size)
  const type = BANNER_TYPES.find((t) => t.id === form.type)
  const platform = BANNER_PLATFORMS.find((p) => p.id === form.platform)
  const profile = getPlatform(form.platform)
  // hasRef 显式传入：refs 是页面的额外字段，buildBannerPrompt 看不到
  const prompt = useMemo(() => buildBannerPrompt({ ...form, hasRef: form.refs.length > 0 }), [form])

  const price = usePricePreview({
    appSlug: 'banner',
    model: form.model,
    outputs: form.outputs,
    resolution: form.resolution,
    aspectRatio: size?.ratio,
  })

  const jobs = jobsFor('banner')
  const outputs = outputsFor('banner')

  // 唯一的硬门槛：没有产品名，平台与文案都无从谈起
  const formOk = form.productName.trim().length > 0
  const canSubmit = formOk

  const doGenerate = () =>
    flow.request({
      info: {
        title: `Banner · ${platform?.name ?? ''}`,
        appSlug: 'banner',
        model: form.model,
        platform: platform?.name,
        outputs: form.outputs,
        aspectRatio: size?.ratio,
        resolution: form.resolution,
        refImages: form.refs.length,
        prompt,
        price: price?.estimatedPrice ?? 0,
        basis: price?.basis,
        free: price?.isFreeThisCall,
      },
      args: {
        appSlug: 'banner',
        title: `Banner · ${form.productName || platform?.name}`,
        sourcePage: 'banner',
        model: form.model,
        prompt,
        inputs: { ...form, __title: `Banner · ${form.productName}` },
        imageUrls: form.refs,
        aspectRatio: size?.ratio,
        resolution: form.resolution,
        outputs: form.outputs,
        estimatedMs: 45000,
      },
    })

  /* -------------------------------- AI 填充 -------------------------------- */

  /**
   * 「AI 填充」：用户只给产品名（有参考图更好），其余文案字段一次补齐。
   * 2026-09-30 用户要求「多一个 AI 填充的按钮，用户只需要有产品名称或者产品图
   * 就可以，填写其他的项目」。
   *
   * 2026-10-01 用户要求「其他页面也加，看看能不能封装一下」—— 逻辑整块搬去了
   * `components/AiFillButton.tsx`（**全站一份实现**，banner 是它最老的调用点）。
   * 这里只剩三样**本页特有**的东西，其余（快照 / 撤销、白名单二次过滤、空结果报错、
   * 失败如实报）都在组件里，不必也不该在这里重写一遍：
   *
   * ① **范围只到「文案四字段」**（主标题 / 副标题 / 卖点 / 补充要求）。
   *    投放平台、场景类型、画布尺寸**一律不动** —— 那是「投在哪」的决策，
   *    用户自己清楚，AI 猜错了他还得逐项改回去，比不填更烦。白名单由
   *    `FILL_FORM_FIELDS.banner` 给（与后端 `_FILL_FIELDS["banner"]` 同一份）。
   * ② **前置条件是产品名**。LLM 是纯文本的（看不了参考图），没有产品名它只能编 ——
   *    编出来的主标题看着像真的，用户会一路用到出图才发现产品是它造的。
   *    所以没产品名时**禁用而不是隐藏**（让「这里能一键补齐」一直看得见）。
   * ③ **画面文字档位是「完全无文字」时也不让点**：那一档图上不会出现任何文字，
   *    AI 写得再好也进不了画面 —— 这个前提必须在前端拦住（提示里说清怎么解），
   *    并且要作为前提写进给模型的上下文（见 `buildBannerFillBrief`）。
   */
  const aiFillBlocked = !form.productName.trim()
    ? '先填产品名称 —— AI 需要知道这是什么产品才写得对'
    : form.textMode === 'none'
      ? '当前档位是「完全无文字」，图上不会出现任何文字 —— 先切到「AI 生成文字」或「用我给的文案」'
      : undefined

  /* --------------------------- 页签 1 · 产品与素材 --------------------------- */

  const stepAssets = (
    <>
      <Card>
        <CardHead title="产品名称" icon="box" hint="先有产品名，后面的平台与文案才有落点" />
        <CardBody>
          <Field
            label="产品名称"
            required
            /* 「从商品库选择」—— 商品库录好的档案一次带进来（标题 + 卖点 + 图组）。
               挂在这一格：产品名是带入的入口字段，也是这一页唯一的必填项。 */
            right={<ProductAssetPickerButton onPick={applyProductAsset} />}
          >
            <Input
              value={form.productName}
              placeholder="如：华数云硅藻土沥水垫"
              onChange={(e) => patch({ productName: e.target.value })}
            />
          </Field>
        </CardBody>
      </Card>

      {/*
        产品参考图 —— **2026-10-09 从本页最底部搬到「产品名称」之后**。
        用户原话（整页截图 + 一句）：「**产品图放上去**」——
        截图里这一块被压在「文案与画面」下面，要滚到最底才看得见。

        为什么落在「产品名称」与「文案与画面」之间，而不是整页最上：
          · 「产品名称」是唯一必填项，也是这一页的入口（进门先答「做什么产品」），
            把它挪下去会让必填项沉到第二屏；
          · 而**参考图必须在写文案之前** —— 右上角那颗「AI 填充」的 brief 里
            带着 `hasRef: form.refs.length > 0`（见下面那张卡的 `buildBannerFillBrief`），
            也就是说「有没有传产品图」是它写文案的依据之一。
            排在文案下面时，用户按顺序填到文案那一步还没传图，
            AI 只能按「没有参考图」写一版 —— 传完图还得回头重填一次。
        ⇒ 顺序改成「先说要做什么（名称）→ 再给实物（参考图）→ 最后写文案」，
          与 AI 填充真正读到的输入顺序一致。
      */}
      <RefUploader
        title="产品参考图（可选）"
        hint="有产品图时强烈建议上传，还原度会显著提升"
        urls={form.refs}
        onChange={(refs) => patch({ refs })}
        max={PRODUCT_REFS_MAX}
        dropLabel="拖拽或点击上传产品图"
        dropHint="支持多选 · 可拖拽排序 · 第一张作为主图"
        emptyHint="未上传参考图时，模型会按文字描述自由发挥产品外观。"
      />
    </>
  )

  /* --------------------------- 页签 2 · 文案与画面 --------------------------- */

  const stepCopy = (
    <>
      {/* v4 上移到第 1 页签 → **v6 又拆出来独立成第 2 页签**（2026-10-09 用户：
          「文案与画面，放到 step2，这块做成 4step」）。
          拆的理由：这块自己就占五个字段（主/副标题、卖点、画面文字档位、补充要求），
          和「产品名称 + 参考图」挤在同一页会把首屏撑爆，而它俩的决策性质也不同
          —— 前者是「我有什么」，后者是「我要说什么」。

          ⚠️ **顺序仍守住 v5 那条约束：参考图排在文案之前**（现在是第 1 步 vs 第 2 步，
          更显式了）。原因见 `stepAssets` 里那段：AI 填充的 brief 里带着 `hasRef`，
          写文案时要读它 —— 用户按顺序填到文案时已经传过图了，不会白写一版。

          hint 里的平台名是**派生值**（真源仍是「平台与版式」那颗 Chips），不新增控件。 */}
      <Card>
        <CardHead
          title="文案与画面"
          icon="text"
          hint={platform ? `图内文字按「${platform.name}」约束 · 换平台在「平台与版式」` : '图内文字语言按所选平台自动约束'}
          right={
            <AiFillButton<typeof DEFAULTS>
              kind="banner"
              fields={FILL_FORM_FIELDS.banner}
              brief={buildBannerFillBrief({ ...form, hasRef: form.refs.length > 0 })}
              sourcePage="banner"
              /* 快照必须在**覆盖前**取 —— 组件里再取就已经是 AI 写过的版本了 */
              snapshot={{
                mainCopy: form.mainCopy,
                subCopy: form.subCopy,
                sellingPoints: form.sellingPoints,
                extra: form.extra,
              }}
              onFill={patch}
              blocked={aiFillBlocked}
            />
          }
        />
        <CardBody>
          <Field label="主标题" hint="留空则由模型按产品自动生成">
            <Input
              value={form.mainCopy}
              placeholder="如：吸水快干 不留水渍"
              onChange={(e) => patch({ mainCopy: e.target.value })}
            />
          </Field>
          <Field label="副标题">
            <Input
              value={form.subCopy}
              placeholder="如：限时 5 折 · 前 100 名加赠"
              onChange={(e) => patch({ subCopy: e.target.value })}
            />
          </Field>
          <Field label="卖点 / 促销信息">
            <Textarea
              value={form.sellingPoints}
              rows={3}
              placeholder="如：0.5s 吸水、可折叠收纳、耐高温 120℃"
              onChange={(e) => patch({ sellingPoints: e.target.value })}
            />
          </Field>
          <Field label="画面文字">
            <Segmented
              value={form.textMode}
              onChange={(v) => patch({ textMode: v })}
              options={TEXT_MODES.map((v) => ({ value: v, label: TEXT_MODE_LABEL[v] }))}
            />
          </Field>
          <Field label="补充要求（可选）">
            <Textarea
              value={form.extra}
              rows={2}
              placeholder="如：暖色木质背景、右上角留出直播间头像位"
              onChange={(e) => patch({ extra: e.target.value })}
            />
          </Field>
        </CardBody>
      </Card>
    </>
  )

  /* --------------------------- 页签 3 · 平台与版式 --------------------------- */

  const stepLayout = (
    <>
      <Card>
        <CardHead title="投放平台" icon="globe" hint="决定图内文字语言与整体视觉调性" />
        <CardBody>
          <Chips
            value={form.platform}
            onChange={(v) => patch({ platform: v })}
            options={BANNER_PLATFORMS.map((p) => ({ value: p.id, label: p.name }))}
          />
           
        </CardBody>
      </Card>

      <Card>
        <CardHead title="场景类型" icon="film" hint="决定信息结构与版式重点" />
        <CardBody tight>
          {/* 9 个场景类型原来竖着铺 9 张带描述的卡（约半屏），宫格后 3 行收完 */}
          <div className="tool-grid">
            {BANNER_TYPES.map((t) => (
              <button
                key={t.id}
                type="button"
                className="tool"
                data-active={form.type === t.id}
                onClick={() => patch({ type: t.id })}
              >
                <div className="tool__name">{t.name}</div>
                <div className="tool__desc">{t.description}</div>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="画布尺寸"
          icon="grid"
          hint={size ? `${size.w}×${size.h}px · ${size.ratio}` : '按投放位置选，别选错再返工'}
        />
        <CardBody>
          <div className="size-ref">
            {BANNER_SIZES.map((s) => (
              <button
                key={s.id}
                type="button"
                className="size-ref__item"
                data-active={form.size === s.id}
                onClick={() => patch({ size: s.id })}
              >
                <div className="size-ref__box" style={{ aspectRatio: s.ratio.replace(':', ' / ') }}>
                  <SmartImage src="" alt="" />
                </div>
                <div className="size-ref__label">{s.name}</div>
                <div className="size-ref__px">
                  {s.w}×{s.h}
                </div>
              </button>
            ))}
          </div>
        </CardBody>
      </Card>
    </>
  )

  /* ------------------------- 页签 4 · 输出与核对 ------------------------- */

  const stepReview = (
    <>
      <Card>
        <CardHead title="输出设置" hint="先在这里比价，再决定用哪个" icon="bolt" />
        <CardBody>
          <ModelSelect kind="image" value={form.model} onChange={(v) => patch({ model: v })} hasRef={form.refs.length > 0} />
          <Field label="分辨率">
            <Segmented
              value={form.resolution}
              onChange={(v) => patch({ resolution: v })}
              options={[
                { value: '1k', label: '1K 快速预览' },
                { value: '2k', label: '2K 上架' },
                { value: '4k', label: '4K 投放' },
              ]}
            />
          </Field>
          <Field label="生成数量">
            <Segmented
              value={String(form.outputs)}
              onChange={(v) => patch({ outputs: Number(v) })}
              options={['1', '2', '4'].map((n) => ({ value: n, label: `${n} 张` }))}
            />
          </Field>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="参数核对" icon="check" hint="确认无误后提交，失败不扣费" />
        <CardBody tight>
          <div className="review-list">
            <div className="review-list__row">
              <span className="review-list__k">投放平台</span>
              <span className="review-list__v">
                {platform?.name ?? '—'}
                <span className="muted"> · 图内文字 {profile.textLanguage}</span>
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">场景类型</span>
              <span className="review-list__v">{type?.name ?? '—'}</span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">画布</span>
              <span className="review-list__v">
                {size?.name ?? '—'} <span className="muted">· {size?.w}×{size?.h}px · {size?.ratio}</span>
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">产品</span>
              <span className={`review-list__v${form.productName.trim() ? '' : ' review-list__v--muted'}`}>
                {form.productName.trim() || '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">主标题</span>
              <span className={`review-list__v${form.mainCopy.trim() ? '' : ' review-list__v--muted'}`}>
                {form.mainCopy.trim() || '未填写（模型自动生成）'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">副标题</span>
              <span className={`review-list__v${form.subCopy.trim() ? '' : ' review-list__v--muted'}`}>
                {form.subCopy.trim() || '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">卖点</span>
              <span className={`review-list__v${form.sellingPoints.trim() ? '' : ' review-list__v--muted'}`}>
                {form.sellingPoints.trim() ? `${form.sellingPoints.split('\n').filter(Boolean).length} 条` : '未填写'}
              </span>
            </div>
            <div className="review-list__row">
              <span className="review-list__k">参考图</span>
              <span className={`review-list__v${form.refs.length ? '' : ' review-list__v--muted'}`}>
                {form.refs.length ? `${form.refs.length} 张` : '未上传'}
              </span>
            </div>
            {/* 模型 / 规格合并成一行：这两项的作用是解释报价，不拆两行占位置 */}
            <div className="review-list__row">
              <span className="review-list__k">模型与规格</span>
              <span className="review-list__v">
                {modelLabel(form.model)}
                <span className="muted">
                  {' '}
                  · {form.outputs} 张 · {form.resolution} · {TEXT_MODE_LABEL[form.textMode] ?? form.textMode}
                </span>
              </span>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  )

  // ⚠️ 这个数组的顺序**必须与 PANES 逐项对应**（分支数 == 页签数、顺序逐项对应）：
  // 两处是同一份契约，加/删/换页签要同时改，`_probe-banner-copy-step.py` 会核对页签名与顺序。
  const stepBody = [stepAssets, stepCopy, stepLayout, stepReview][Math.min(step, PANES.length - 1)]

  /* ------------------------------ 底部导航 ------------------------------ */

  const submitDock = (
    <Card className="studio__dock">
      <CardBody tight>
        <SubmitBar
          price={price?.estimatedPrice ?? 0}
          basis={price?.basis}
          free={price?.isFreeThisCall}
          disabled={!canSubmit}
          disabledReason={!canSubmit ? '请先在「产品与素材」填写产品名称' : undefined}
          label="生成 Banner"
          onGenerate={doGenerate}
        />
      </CardBody>
    </Card>
  )

  return (
    <div className="page page--wide">
      <GeneratorFrame
        form={
          <WorkflowTabs tabs={PANES} current={step} onChange={setStep}>
            {stepBody}
          </WorkflowTabs>
        }
        panel={
          <>
            {submitDock}
            {/* 右栏不再挂「尺寸参考」—— 画布尺寸已收进左栏第 2 页签，同一份 state 不做两个控件。
                ⚠️ 这条注释 2026-10-09 之前是**裸写的行注释标记**（没包花括号），React 把它当
                **文本节点**渲染出来了 —— 用户在右栏看到一行「斜杠斜杠 右栏不再挂…」的乱码。
                JSX 子节点里的注释一律要用花括号包住，裸写注释标记会直接显示在页面上。 */}
            <StudioPanel
              jobs={jobs}
              outputs={outputs}
              onCancel={cancel}
              onClear={clearResults}
              activeHint="Banner 单张约 45 秒，可以先去别的页面干活"
              resultTitle="Banner 结果"
              resultEmpty={`当前配置：${platform?.name ?? ''} · ${type?.name ?? ''} · ${size?.name ?? ''} ${size?.ratio ?? ''}。确认参数后点「生成 Banner」。`}
              aspectRatio={size?.ratio}
            />
          </>
        }
      />

      <PriceConfirmDialog info={flow.pending} onCancel={flow.cancel} onConfirm={flow.confirm} />
    </div>
  )
}
